import { httpClient } from '../../lib/utils';
import { envValue } from '../../config/envUtils';
import { SAVE_LEVEL } from '../constants/Level';
import { configurationSystemDefault } from '../constants/SystemDefaults';
import { Service } from './Service';
import store from '../store/Store';
import * as ActionTypes from '../constants/ActionTypes';
import moment from 'moment';
import { assign, get, find } from 'lodash';
import { postResponseToSplunkFailed, postResponseToSplunkSuccess } from './logging/splunklogging';

const SOURCEURL = envValue('SOURCEURL');
const FRONTMAN_CHANNEL_URL = envValue('FRONTMAN_CHANNEL_URL');
const OFFERS_URL = envValue('OFFERS_URL');
const SBEM_CLEAR_CACHE_URL = envValue('SBEM_CLEAR_CACHE_URL');

function newOrUpdate(mode = 'new', chainId, hotelId, themeCode, content, saveLevel = SAVE_LEVEL.HOTEL) {
    return httpClient[mode === 'new' ? 'post' : 'put'](
        `${SOURCEURL}/shs-bedesigner-services/v1/contents/configuration?${ConfigurationService.paramProxy(
            chainId,
            hotelId,
            null,
            saveLevel
        )}&contentcode=${themeCode}&clearcache=all`,
        content
    );
}

/**
 * Fetches the product groups/categories configured for an SRS hotel
 *
 * @param {Object} params
 * @param {String} params.languageCode
 * @param {String} params.hotelId
 * @returns {Promise}
 */
export function fetchRetailProductCategories(params) {
    const languageCode = get(params, 'languageCode') || 'en-US';
    const hotelId = get(params, 'hotelId');

    if (!hotelId) {
        return Promise.resolve(null);
    }

    const vendorStoreId = `SYNHOTEL:${hotelId}`
    return httpClient
        .get(`${OFFERS_URL}/categories?vendorStoreId=${vendorStoreId}&language=${languageCode}&offeringTypeCd=STD`).then(
            response => {
                const categories = get(response, 'data.categories') || []
                store.dispatch({
                    type: ActionTypes.SET_RETAIL_PRODUCT_CATEGORIES,
                    categories
                })
                Promise.resolve(categories)
            })
        .catch(() => {
            store.dispatch({
                type: ActionTypes.SET_RETAIL_PRODUCT_CATEGORIES,
                categories: null
            })
            Promise.resolve(null)
        })
}

export function checkIfTemplateExists(params) {
    const chainId = get(params, 'chainId');
    const hotelId = get(params, 'hotelId');
    const templateCode = get(params, 'templateCode');
    return httpClient
        .get(`${FRONTMAN_CHANNEL_URL}/template?chainId=${chainId}&hotelId=${hotelId}&templateCode=${templateCode}`)
        .then(response => {
            const templates = get(response, 'data.templates', []);
            const foundTemplate = find(templates, item => {
                return get(item, 'code') === templateCode;
            });
            return foundTemplate;
        });
}

export function clearSbemConfigCache(chainId, hotelId, configCode, saveLevel = SAVE_LEVEL.HOTEL) {
    return httpClient.post(SBEM_CLEAR_CACHE_URL, { chainId, hotelId, configCode, saveLevel, keyType: 'config' });
}

export function saveTemplateToFrontman({ code, name, active = true, chainId, hotelId, saveLevel }) {
    const payload = {};
    const template = { code, active, type: 'Sbe' };
    const chain = {};
    const hotel = {};
    const level = !!saveLevel ? saveLevel : SAVE_LEVEL.HOTEL;
    const requestTime = moment().valueOf();
    if (name) {
        assign(template, { name });
    }
    if (chainId && level === SAVE_LEVEL.CHAIN) {
        assign(chain, { id: chainId });
        assign(template, { chain });
    }
    if (hotelId && level === SAVE_LEVEL.HOTEL) {
        assign(hotel, { id: hotelId });
        assign(template, { hotel });
    }
    assign(payload, { templates: [template] });
    store.dispatch({ type: ActionTypes.TEMPLATE_SAVE_REQUEST });
    return httpClient
        .post(`${FRONTMAN_CHANNEL_URL}/template`, payload)
        .then(response => {
            store.dispatch({ type: ActionTypes.TEMPLATE_SAVE_SUCCESS });
            postResponseToSplunkSuccess(
                response.config.method,
                response.config.url,
                (moment().valueOf() - requestTime) / 1000 + 'sec',
                response.status
            );
        })
        .catch(error => {
            const response = error.response;
            store.dispatch({ type: ActionTypes.TEMPLATE_SAVE_FAILURE });
            postResponseToSplunkFailed(
                response.config.method,
                response.config.url,
                (moment().valueOf() - requestTime) / 1000 + 'sec',
                error.message
            );
        });
}

export class ConfigurationService extends Service {
    /**
     * Gets list of themes at given save level
     * @param chainId
     * @param hotelId
     * @param saveLevel
     * @returns {Promise}
     */
    static list(chainId, hotelId, saveLevel = SAVE_LEVEL.HOTEL) {
        return httpClient.get(
            `${SOURCEURL}/shs-bedesigner-services/v1/contents/configuration/list?${ConfigurationService.paramProxy(
                chainId,
                hotelId,
                null,
                saveLevel
            )}&clearcache=all`
        );
    }

    /**
     * Gets single theme config at given save level
     * @param chainId
     * @param hotelId
     * @param contentCode
     * @param saveLevel
     * @returns {Promise}
     */
    static get(chainId, hotelId, contentCode, saveLevel = SAVE_LEVEL.HOTEL) {
        return httpClient.get(
            `${SOURCEURL}/shs-bedesigner-services/v1/contents/configuration?${ConfigurationService.paramProxy(
                chainId,
                hotelId,
                null,
                saveLevel
            )}&contentcode=${contentCode}&clearcache=all`,
            {}
        );
    }

    static delete(chainId, hotelId, contentCode, saveLevel = SAVE_LEVEL.HOTEL) {
        return httpClient.delete(
            `${SOURCEURL}/shs-bedesigner-services/v1/contents/configuration?${ConfigurationService.paramProxy(
                chainId,
                hotelId,
                null,
                saveLevel
            )}&contentcode=${contentCode}&clearcache=all`,
            {
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );
    }
    static all(chainId, hotelId, contentCode, saveLevel = SAVE_LEVEL.HOTEL) {
        return httpClient.get(
            `${SOURCEURL}/shs-bedesigner-services/v1/contents/configuration/all?${ConfigurationService.paramProxy(
                chainId,
                hotelId,
                null,
                saveLevel
            )}&contentcode=${contentCode}&clearcache=all`
        );
    }
    static setActive(chainId, hotelId, contentCode, value, saveLevel = SAVE_LEVEL.HOTEL) {
        return httpClient.put(
            `${SOURCEURL}/shs-bedesigner-services/v1/contents/configuration/active?${ConfigurationService.paramProxy(
                chainId,
                hotelId,
                null,
                saveLevel
            )}&contentcode=${contentCode}&active=${value}&clearcache=all`,
            {}
        );
    }
    static setAsDefault(chainId, hotelId, contentCode, saveLevel = SAVE_LEVEL.HOTEL) {
        return httpClient.put(
            `${SOURCEURL}/shs-bedesigner-services/v1/contents/configuration/default?${ConfigurationService.paramProxy(
                chainId,
                hotelId,
                null,
                saveLevel
            )}&contentcode=${contentCode}&clearcache=all`,
            {}
        );
    }
    /**
     * Get systemdefault themes
     */
    static systemdefault() {
        return Promise.resolve(configurationSystemDefault);
    }

    /**
     * Helper function for reloading data on certain SAVE_LEVEL
     */
    static reload(chainId, hotelId, saveLevel = SAVE_LEVEL.HOTEL, swtichToCode, dontMarkAsDefault) {
        let defaultContent = {
            level: SAVE_LEVEL.SYSTEM,
            code: 'systemdefault'
        };
        let mainPromise = Promise.resolve([
            {
                ContentCode: 'systemdefault',
                level: SAVE_LEVEL.SYSTEM,
                IsActive: true,
                MetaData: {
                    Timestamp: moment.utc().valueOf(),
                    Comment: 'systemdefault'
                }
            }
        ]);
        /**
         * Gets list from chain level, or fail gracefully
         * @type {Promise.<TResult>}
         */
        mainPromise = mainPromise.then(upperLevelArray => {
            return ConfigurationService.list(chainId, hotelId, SAVE_LEVEL.CHAIN).then(
                response => {
                    if (response.data.Result.ResultCode === 'SUCCESS') {
                        defaultContent = {
                            level: SAVE_LEVEL.CHAIN,
                            code: response.data.BEDesignerResult.Value.DefaultContentCode
                        };
                        return [
                            ...upperLevelArray,
                            ...response.data.BEDesignerResult.Value.ContentCodes.map(elem => {
                                elem['level'] = SAVE_LEVEL.CHAIN;
                                return elem;
                            })
                        ];
                    }
                },
                () => upperLevelArray
            );
        });
        /**
         * Gets list from save level, or fail gracefully
         * @type {Promise.<TResult>}
         */
        if (saveLevel !== SAVE_LEVEL.CHAIN) {
            mainPromise = mainPromise.then(upperLevelArray => {
                return ConfigurationService.list(chainId, hotelId, saveLevel).then(
                    response => {
                        if (response.data.Result.ResultCode === 'SUCCESS') {
                            defaultContent = {
                                level: saveLevel,
                                code: response.data.BEDesignerResult.Value.DefaultContentCode
                            };
                            return [
                                ...upperLevelArray,
                                ...response.data.BEDesignerResult.Value.ContentCodes.map(elem => {
                                    elem['level'] = saveLevel;
                                    return elem;
                                })
                            ];
                        }
                    },
                    () => upperLevelArray
                );
            });
        }
        // List is now accumulated in mainPromise
        return mainPromise.then(configList => {
            //Switching logic
            if (swtichToCode !== undefined) {
                defaultContent = {
                    level: saveLevel,
                    code: swtichToCode
                };
            }
            return (defaultContent.level === SAVE_LEVEL.SYSTEM
                ? ConfigurationService.systemdefault()
                : ConfigurationService.get(chainId, hotelId, defaultContent.code, defaultContent.level).then(
                      response => {
                          return response.data.BEDesignerResult.Value.Data;
                      }
                  )
            )
                .catch(() => {
                    // TODO: possible error handling here
                    return ConfigurationService.systemdefault();
                })
                .then(config => {
                    if (!dontMarkAsDefault) {
                        store.dispatch({ type: ActionTypes.ADD_CONFIG_LIST, configList, defaultContent });
                    } else {
                        store.dispatch({ type: ActionTypes.ADD_CONFIG_LIST, configList, defaultContent: false });
                    }
                    store.dispatch({ type: ActionTypes.LOGIN_SET_CONFIG, config, code: defaultContent.code });
                    return config;
                });
        });
    }
}

ConfigurationService.update = newOrUpdate.bind(null, 'update');
ConfigurationService.new = newOrUpdate.bind(null, 'new');
ConfigurationService.contenttype = 'configuration';



// WEBPACK FOOTER //
// ./src/services/ConfigurationService.js